import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './TeacherDashboard.css';
import LogoutButton from './LogoutButton';

const API = 'https://clarytix-backend.onrender.com';
const CLASS_OPTIONS = Array.from({ length: 8 }, (_, i) => `Class ${i + 5}`);

function ClassSelect({ value, onChange }) {
    return (
        <select className="dropdown" value={value} onChange={(e) => onChange(e.target.value)}>
            <option value="">Class</option>
            {CLASS_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
    );
}

function SectionSelect({ sections, value, onChange, disabled }) {
    return (
        <select className="dropdown" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
            <option value="">Section</option>
            {sections.map(s => <option key={s.id} value={s.id}>{s.section_name}</option>)}
        </select>
    );
}

// ─────────────────────────────────────────────────────────────
// Assign Quiz card (self-contained: available-* endpoints + POST)
// ─────────────────────────────────────────────────────────────
function AssignQuizCard({ schoolId, teacherId }) {
    const [cls, setCls] = useState('');
    const [sections, setSections] = useState([]);
    const [sectionId, setSectionId] = useState('');
    const [subjects, setSubjects] = useState([]);
    const [subject, setSubject] = useState('');
    const [chapters, setChapters] = useState([]);
    const [chapterId, setChapterId] = useState('');
    const [topics, setTopics] = useState([]);
    const [topicId, setTopicId] = useState('');
    const [message, setMessage] = useState('');
    const [messageColor, setMessageColor] = useState('green');

    useEffect(() => {
        if (cls) {
            fetch(`${API}/admin/sections?schoolId=${schoolId}&className=${cls}`)
                .then(r => r.json()).then(d => { setSections(d.success ? d.sections : []); setSectionId(''); });
            fetch(`${API}/admin/subjects?schoolId=${schoolId}&className=${cls}`)
                .then(r => r.json()).then(d => {
                    setSubjects(d.success ? d.subjects : []);
                    setSubject(''); setChapters([]); setChapterId(''); setTopics([]); setTopicId('');
                });
        } else {
            setSections([]); setSectionId(''); setSubjects([]); setSubject('');
            setChapters([]); setChapterId(''); setTopics([]); setTopicId('');
        }
    }, [cls, schoolId]);

    useEffect(() => {
        if (cls && subject) {
            fetch(`${API}/admin/available-chapters?schoolId=${schoolId}&className=${cls}&subjectId=${subject}`)
                .then(r => r.json()).then(d => { setChapters(d.success ? d.chapters : []); setChapterId(''); setTopics([]); setTopicId(''); });
        } else { setChapters([]); setChapterId(''); setTopics([]); setTopicId(''); }
    }, [cls, subject, schoolId]);

    useEffect(() => {
        if (chapterId) {
            fetch(`${API}/admin/available-topics?schoolId=${schoolId}&chapterId=${chapterId}`)
                .then(r => r.json()).then(d => { setTopics(d.success ? d.topics : []); setTopicId(''); });
        } else { setTopics([]); setTopicId(''); }
    }, [chapterId, schoolId]);

    const showMessage = (text, color = 'green') => { setMessageColor(color); setMessage(text); setTimeout(() => setMessage(''), 3000); };
    const clear = () => { setCls(''); setSectionId(''); setSubject(''); setChapterId(''); setTopicId(''); setTopics([]); setChapters([]); };

    const handleSend = () => {
        fetch(`${API}/teacher/assign-quiz`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ schoolId, className: cls, subjectId: subject, topicId, teacherId, sectionId: sectionId || null })
        })
            .then(r => r.json())
            .then(d => {
                if (d.success) showMessage('Quiz sent successfully!', 'green');
                else if (d.message === 'Quiz already assigned.') showMessage('Quiz has already been sent.', 'red');
                else showMessage('Failed to send quiz. Try again.', 'red');
                clear();
            })
            .catch(() => { showMessage('Server error. Try again.', 'red'); clear(); });
    };

    return (
        <div className="card teacher-card">
            <h3 className="card-title">Assign Quiz</h3>
            <div className="dropdown-row">
                <ClassSelect value={cls} onChange={setCls} />
                <SectionSelect sections={sections} value={sectionId} onChange={setSectionId} disabled={!cls} />
                <select className="dropdown" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!cls}>
                    <option value="">Subject</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select className="dropdown" value={chapterId} onChange={(e) => setChapterId(e.target.value)} disabled={!subject || chapters.length === 0}>
                    <option value="">Chapter</option>
                    {chapters.map(ch => <option key={ch.id} value={ch.id}>{ch.chapter_name}</option>)}
                </select>
                <select className="dropdown" value={topicId} onChange={(e) => setTopicId(e.target.value)} disabled={!chapterId}>
                    <option value="">Topic</option>
                    {topics.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
            </div>
            <div className="card-actions">
                <button className="track-btn" disabled={!cls || !subject || !topicId} onClick={handleSend}>Send Quiz</button>
            </div>
            {message && <div className="msg" style={{ color: messageColor }}>{message}</div>}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Topic picker card (Track Topic Wise & View Questions share this)
// uses assigned quizzes: /admin/chapters + /admin/assigned-topics
// ─────────────────────────────────────────────────────────────
function TopicPickerCard({ title, buttonLabel, schoolId, onSubmit }) {
    const [cls, setCls] = useState('');
    const [sections, setSections] = useState([]);
    const [sectionId, setSectionId] = useState('');
    const [subjects, setSubjects] = useState([]);
    const [subject, setSubject] = useState('');
    const [chapters, setChapters] = useState([]);
    const [chapterId, setChapterId] = useState('');
    const [topics, setTopics] = useState([]);
    const [topicId, setTopicId] = useState('');

    useEffect(() => {
        if (cls) {
            fetch(`${API}/admin/sections?schoolId=${schoolId}&className=${cls}`)
                .then(r => r.json()).then(d => { setSections(d.success ? d.sections : []); setSectionId(''); });
            fetch(`${API}/admin/subjects?schoolId=${schoolId}&className=${cls}`)
                .then(r => r.json()).then(d => {
                    setSubjects(d.success ? d.subjects : []);
                    setSubject(''); setChapters([]); setChapterId(''); setTopics([]); setTopicId('');
                });
        } else {
            setSections([]); setSectionId(''); setSubjects([]); setSubject('');
            setChapters([]); setChapterId(''); setTopics([]); setTopicId('');
        }
    }, [cls, schoolId]);

    useEffect(() => {
        if (cls && subject) {
            fetch(`${API}/admin/chapters?schoolId=${schoolId}&className=${cls}&subjectId=${subject}`)
                .then(r => r.json()).then(d => { setChapters(d.success ? d.chapters : []); setChapterId(''); setTopics([]); setTopicId(''); });
        } else { setChapters([]); setChapterId(''); setTopics([]); setTopicId(''); }
    }, [cls, subject, schoolId]);

    useEffect(() => {
        if (cls && subject && chapterId) {
            let url = `${API}/admin/assigned-topics?schoolId=${schoolId}&className=${cls}&subjectId=${subject}&chapterId=${chapterId}`;
            if (sectionId) url += `&sectionId=${sectionId}`;
            fetch(url).then(r => r.json()).then(d => { setTopics(d.success ? d.topics : []); setTopicId(''); });
        } else { setTopics([]); setTopicId(''); }
    }, [cls, subject, chapterId, sectionId, schoolId]);

    return (
        <div className="card">
            <h3 className="card-title">{title}</h3>
            <div className="dropdown-row">
                <ClassSelect value={cls} onChange={setCls} />
                <SectionSelect sections={sections} value={sectionId} onChange={setSectionId} disabled={!cls} />
                <select className="dropdown" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!cls}>
                    <option value="">Subject</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select className="dropdown" value={chapterId} onChange={(e) => setChapterId(e.target.value)} disabled={!subject}>
                    <option value="">Chapter</option>
                    {chapters.map(ch => <option key={ch.id} value={ch.id}>{ch.chapter_name}</option>)}
                </select>
                <select className="dropdown" value={topicId} onChange={(e) => setTopicId(e.target.value)} disabled={!chapterId}>
                    <option value="">Topic</option>
                    {topics.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
            </div>
            <div className="card-actions">
                <button className="track-btn" disabled={!topicId} onClick={() => onSubmit({ topicId, sectionId })}>{buttonLabel}</button>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Track Performance Student Wise card (self-contained)
// ─────────────────────────────────────────────────────────────
function StudentPerfCard({ schoolId, onSubmit }) {
    const [cls, setCls] = useState('');
    const [sections, setSections] = useState([]);
    const [sectionId, setSectionId] = useState('');
    const [students, setStudents] = useState([]);
    const [student, setStudent] = useState('');
    const [subjects, setSubjects] = useState([]);
    const [subject, setSubject] = useState('');

    useEffect(() => {
        if (cls) {
            fetch(`${API}/admin/sections?schoolId=${schoolId}&className=${cls}`)
                .then(r => r.json()).then(d => { setSections(d.success ? d.sections : []); setSectionId(''); });
        } else { setSections([]); setSectionId(''); }
    }, [cls, schoolId]);

    useEffect(() => {
        if (cls) {
            const qp = new URLSearchParams({ schoolId, className: cls });
            if (sectionId) qp.append('sectionId', sectionId);
            fetch(`${API}/admin/students?${qp.toString()}`)
                .then(r => r.json()).then(d => {
                    setStudents(d.success ? d.students : []);
                    setStudent(''); setSubjects([]); setSubject('');
                });
        } else { setStudents([]); setStudent(''); setSubjects([]); setSubject(''); }
    }, [cls, sectionId, schoolId]);

    useEffect(() => {
        if (student) {
            fetch(`${API}/admin/student-subjects?studentId=${student}`)
                .then(r => r.json()).then(d => { setSubjects(d.success ? d.subjects : []); setSubject(''); });
        } else { setSubjects([]); setSubject(''); }
    }, [student]);

    return (
        <div className="card">
            <h3 className="card-title">Track Performance Student Wise</h3>
            <div className="dropdown-row">
                <ClassSelect value={cls} onChange={setCls} />
                <SectionSelect sections={sections} value={sectionId} onChange={setSectionId} disabled={!cls} />
                <select className="dropdown" value={student} onChange={(e) => setStudent(e.target.value)} disabled={!cls}>
                    <option value="">Student</option>
                    {students.map(s => <option key={s.id} value={s.id}>{s.username}</option>)}
                </select>
                <select className="dropdown" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!student}>
                    <option value="">Subject</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
            </div>
            <div className="card-actions">
                <button className="track-btn" disabled={!student || !subject} onClick={() => onSubmit({ student, subject })}>Track Performance</button>
            </div>
        </div>
    );
}

function TeacherDashboard() {
    const schoolId = localStorage.getItem('schoolId');
    const schoolLogo = localStorage.getItem('schoolLogoUrl');
    const teacherId = localStorage.getItem('userId');
    const navigate = useNavigate();

    const sq = sectionId => (sectionId ? `?sectionId=${sectionId}` : '');

    return (
        <div className="teacher-dashboard-wrapper">
            <div className="teacher-dashboard-container">
                <img src={schoolLogo} alt="School Logo" className="school-logo-large" />

                <AssignQuizCard schoolId={schoolId} teacherId={teacherId} />

                <TopicPickerCard
                    title="Track Performance Topic Wise" buttonLabel="Track Performance" schoolId={schoolId}
                    onSubmit={({ topicId, sectionId }) => navigate(`/admin/performance/${topicId}${sq(sectionId)}`)} />

                <StudentPerfCard schoolId={schoolId}
                    onSubmit={({ student, subject }) => navigate(`/admin/student-performance?studentId=${student}&subjectId=${subject}`)} />

                <TopicPickerCard
                    title="View Questions" buttonLabel="View Questions" schoolId={schoolId}
                    onSubmit={({ topicId, sectionId }) => navigate(`/admin/view-questions/${topicId}${sq(sectionId)}`)} />

                <div className="logout-container">
                    <LogoutButton />
                </div>
            </div>
        </div>
    );
}

export default TeacherDashboard;
